import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import {
  netWorkingCapitalRequiredAtClosingAtom,
  periodFormatIdAtom,
  periodFormatOptions,
  workingCapitalEpochsAtomsContainer,
  workingCapitalFormulaIdAtom,
  workingCapitalFormulaOptions,
} from "@/financeModels/owasco/workingCapital";
import { Card, Stack, Text } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import { TbCash } from "react-icons/tb";
import {
  ComputedDollarResult,
  ComputedResultContainer,
  ComputedResultLine,
} from "../../Sidebar/ComputedResult";
import { InputSelect } from "../../Sidebar/InputSelect";

const { orderedEpochsMolecule } = workingCapitalEpochsAtomsContainer;

const Panel = () => {
  const reqAtClose = useAtomValue(netWorkingCapitalRequiredAtClosingAtom);
  const [workingCapitalFormulaId, setWorkingCapitalFormulaId] = useAtom(
    workingCapitalFormulaIdAtom,
  );

  const [periodFormatId, setPeriodFormatId] = useAtom(periodFormatIdAtom);

  const currentOption =
    workingCapitalFormulaOptions.find(
      (option) => option.id === workingCapitalFormulaId,
    ) || workingCapitalFormulaOptions[0];

  return (
    <Stack gap={4}>
      <InputSelect
        labelWidth={174}
        label={
          <LabelExplainer
            label="Data Format (Bal. Sheet)"
            explanation={
              <Doc path="@/docs/pages/model/working-capital/sidebar/nwc-periodFormat.mdx" />
            }
          />
        }
        data={periodFormatOptions.map((option) => ({
          value: option.id,
          label: option.label,
        }))}
        value={periodFormatId}
        onChange={(value) =>
          setPeriodFormatId(
            (value ||
              periodFormatOptions[0]
                .id) as (typeof periodFormatOptions)[number]["id"],
          )
        }
      />
      <InputSelect
        labelWidth={174}
        label={
          <LabelExplainer
            label="Aggregation Method"
            explanation={
              <Doc path="@/docs/pages/model/working-capital/sidebar/nwc-formula.mdx" />
            }
          />
        }
        data={workingCapitalFormulaOptions.map((option) => ({
          value: option.id,
          label: option.label,
        }))}
        value={workingCapitalFormulaId}
        onChange={(value) =>
          setWorkingCapitalFormulaId(
            (value ||
              workingCapitalFormulaOptions[0]
                .id) as (typeof workingCapitalFormulaOptions)[number]["id"],
          )
        }
      />
      <Card padding="sm" bg={"infomatic.0"}>
        <Text size="sm" lh={1.4}>
          <b>{currentOption.title}:</b> {currentOption.description}
        </Text>
      </Card>
      <TTMWarning />

      <ComputedResultContainer>
        <ComputedResultLine
          label={
            <LabelExplainer
              label="WC Required at Closing"
              explanation={
                <Doc path="@/docs/pages/model/working-capital/sidebar/nwc-req-at-close.mdx" />
              }
            />
          }
          value={<ComputedDollarResult value={reqAtClose} />}
        />
      </ComputedResultContainer>
    </Stack>
  );
};

export const TTMWarning = () => {
  const workingCapitalFormulaId = useAtomValue(workingCapitalFormulaIdAtom);
  const periodFormatId = useAtomValue(periodFormatIdAtom);
  const orderedEpochsAtoms = useAtomValue(orderedEpochsMolecule);
  let message = "";
  if (workingCapitalFormulaId !== "ttm") return null;
  if (orderedEpochsAtoms.length < 12) {
    message = `TTM requires at least 12 periods. Without 12 periods, the TTM calculation will average over the periods provided.`;
  }
  if (periodFormatId !== "monthly") {
    message = `TTM requires monthly periods. Without monthly periods, the TTM calculation will average over the periods provided. It will not be a monthy average.`;
  }

  if (!message) return null;

  return (
    <Card padding="sm" bg={"yellow.0"}>
      <Text size="sm" lh={1.4}>
        {message}
      </Text>
    </Card>
  );
};

export const ModuleNetWorkingCapital = {
  key: "Net Working Capital",
  icon: <TbCash />,
  control: "Net Working Capital",
  panel: <Panel />,
};
